import React, { useState, useEffect } from "react";
import { Card, Table, Button, Alert, Badge, Modal } from "react-bootstrap";
import { getContacts, deleteContact } from "../../services/api";
import "./ContactManagement.css";

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewContact, setViewContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getContacts();
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (err) {
      setError("Gagal memuat data pesan kontak");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteContact(deleteId);
      setContacts((prev) => prev.filter((c) => c.id !== deleteId));
      setDeleteId(null);
    } catch (err) {
      setError("Gagal menghapus pesan");
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="contact-management-page">
      {/* Page Header */}
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-envelope-open-text"></i>
            </div>
            <div>
              <h1 className="page-header-title">Pesan Kontak</h1>
              <p className="page-header-subtitle">
                Daftar pesan yang masuk dari halaman kontak website
              </p>
            </div>
          </div>
          <Badge className="contact-count-badge">{contacts.length} Pesan</Badge>
        </div>
      </div>

      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError(null)}
          className="mt-3"
        >
          {error}
        </Alert>
      )}

      <Card className="content-card mt-3">
        <Card.Body className="p-0">
          {loading ? (
            <div className="loading-state">
              <div className="spinner-border text-primary" role="status" />
              <p className="mt-3 text-muted">Memuat data pesan...</p>
            </div>
          ) : contacts.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox"></i>
              <p>Belum ada pesan masuk</p>
            </div>
          ) : (
            <Table responsive className="contact-table" hover>
              <thead>
                <tr>
                  <th style={{ width: "50px" }}>#</th>
                  <th>Tanggal</th>
                  <th>Nama</th>
                  <th>Telepon</th>
                  <th>Email</th>
                  <th>Pesan</th>
                  <th style={{ width: "80px" }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, index) => (
                  <tr key={contact.id}>
                    <td className="text-muted">{index + 1}</td>
                    <td className="td-tanggal">{contact.tanggal}</td>
                    <td className="td-nama">{contact.nama}</td>
                    <td className="td-telpon">{contact.telpon}</td>
                    <td className="td-email">
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </td>
                    <td className="td-pesan">
                      <span className="pesan-preview">{contact.pesan}</span>
                      <Button
                        variant="link"
                        size="sm"
                        className="btn-lihat"
                        onClick={() => setViewContact(contact)}
                      >
                        Lihat
                      </Button>
                    </td>
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        className="btn-delete"
                        onClick={() => setDeleteId(contact.id)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* View Detail Modal */}
      <Modal
        show={!!viewContact}
        onHide={() => setViewContact(null)}
        centered
        size="md"
      >
        <Modal.Header
          style={{
            background:
              "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 100%)",
            color: "white",
          }}
        >
          <Modal.Title>
            <i className="fas fa-envelope me-2"></i>
            Detail Pesan
          </Modal.Title>
          <button
            className="btn-close btn-close-white"
            onClick={() => setViewContact(null)}
          />
        </Modal.Header>
        {viewContact && (
          <Modal.Body className="p-4">
            <div className="detail-item">
              <label>Tanggal</label>
              <p>{viewContact.tanggal}</p>
            </div>
            <div className="detail-item">
              <label>Nama</label>
              <p>{viewContact.nama}</p>
            </div>
            <div className="detail-item">
              <label>Telepon</label>
              <p>{viewContact.telpon}</p>
            </div>
            <div className="detail-item">
              <label>Email</label>
              <p>
                <a href={`mailto:${viewContact.email}`}>{viewContact.email}</a>
              </p>
            </div>
            <div className="detail-item">
              <label>Pesan</label>
              <p className="pesan-full">{viewContact.pesan}</p>
            </div>
          </Modal.Body>
        )}
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setViewContact(null)}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        show={!!deleteId}
        onHide={() => setDeleteId(null)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>Hapus Pesan</Modal.Title>
        </Modal.Header>
        <Modal.Body>Yakin ingin menghapus pesan ini?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteId(null)}>
            Batal
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Menghapus..." : "Hapus"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ContactManagement;
